import { useState } from 'react'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import {
  PerDiemWizardProvider,
  usePerDiemWizard,
} from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'
import { PerDiemWizardData } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/types'

import CompanyPaidItems from './_CompanyPaidItems'
import ReviewAndSubmit from './_ReviewAndSubmit'
import TripDetails from './_TripDetails'

const formatCurrency = (amountCents: number, currency: string): string => {
  const amount = amountCents / 100
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
  }).format(amount)
}

interface PerDiemWizardModalProps {
  onClose: () => void
}

interface PerDiemWizardModalContentProps {
  onClose: () => void
  locked?: boolean
}

const PerDiemWizardModalContentWithLock = ({ onClose, locked }: PerDiemWizardModalContentProps) => {
  const { data, calculation } = usePerDiemWizard()

  const initialData: PerDiemWizardData = data

  const footerMessage =
    calculation.totalAmountCents > 0
      ? `${formatCurrency(calculation.totalAmountCents, calculation.currency)} ${i18n.t(
          'expenses_v2.forms.germany_per_diem_modal.per_diem_total'
        )}`
      : calculation.isCalculating
      ? i18n.t('common.loading')
      : ''

  const hasValidDestination = data.destinations.some((dest) => dest.locationData.country)

  const tripDetailsStep: WizardStep<PerDiemWizardData> = {
    title: i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step1.title'),
    nextText: i18n.t('common.next'),
    render: () => <TripDetails />,
    isCompleted: () => Boolean(data.startDate && data.endDate && hasValidDestination),
    footerMessage,
  }

  const companyPaidItemsStep: WizardStep<PerDiemWizardData> = {
    title: i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step2.title'),
    nextText: i18n.t('common.next'),
    render: () => <CompanyPaidItems />,
    isCompleted: () => true,
    footerMessage,
  }

  const reviewSubmitStep: WizardStep<PerDiemWizardData> = {
    title: i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.title'),
    nextText: i18n.t('common.save'),
    render: () => <ReviewAndSubmit />,
    isCompleted: () => true,
    footerMessage,
  }

  const handleSubmit = async () => {
    // TODO: Implement submit logic with GraphQL mutation
  }

  return (
    <Wizard.Modal
      title={i18n.t('expenses_v2.forms.germany_per_diem_modal.title')}
      steps={[tripDetailsStep, companyPaidItemsStep, reviewSubmitStep]}
      initialData={initialData}
      onSubmit={handleSubmit}
      onClose={onClose}
      size="large"
      locked={locked}
    />
  )
}

export const PerDiemWizardModal = ({ onClose }: PerDiemWizardModalProps) => {
  // Lock the modal initially to prevent auto-close from stale events
  // This is a workaround for a known Wizard.Modal bug
  const [locked, setLocked] = useState(true)

  useEffect(() => {
    // Unlock after mount to allow normal close behavior
    const timer = setTimeout(() => {
      setLocked(false)
    }, 200)
    return () => clearTimeout(timer)
  }, [])

  return (
    <PerDiemWizardProvider>
      <PerDiemWizardModalContentWithLock onClose={onClose} locked={locked} />
    </PerDiemWizardProvider>
  )
}
